// Imperative Google Maps rendering for the pathfinder: building polygons at
// the correct per-building floor, route polylines split into legs, the flowing
// direction chevrons on the active leg, floor-switch overlays at shaft
// transitions, and start/end/user markers.

import { useEffect, useRef } from 'react';
import { legFloors } from './lib/route.js';
import { bearingDeg, haversineMeters } from './lib/geo.js';
import { createFloorSwitchOverlay, createBuildingTransitionOverlay, createBuildingLabelOverlay, createParkingLotOverlay } from './components/floorSwitchOverlay.js';
import { createRoomInfoOverlay } from './components/roomInfoOverlay.js';
import { createEventPinOverlay } from './components/eventPinOverlay.js';
import { DETAIL_ZOOM } from './lib/zoom.js';
import { BIKE_COLOR } from './lib/bikes.js';

const HIGHLIGHT = { fill: '#fbbc04', fillOp: 0.55, stroke: '#f29900', strokeW: 3 };
// The room walked FROM, in the green of its own Start pill — so the two ends of
// the route are told apart by colour at a glance, and each room matches the
// marker naming it. The destination keeps the amber it has always had, which is
// also what an explored room shows before any route exists.
const START_HIGHLIGHT = { fill: '#34a853', fillOp: 0.5, stroke: '#188038', strokeW: 3 };
// A scanned sign marks a START, so it wears the start's green — the route's own
// Start pill lands on this exact point a moment later, and the colour is what
// says the two are the same place rather than two things to reconcile.
const SCAN_COLOR = '#34a853';
// Study spots / libraries get their own colour so a previewed place is never
// mistaken for the routing destination.
const PLACE_HIGHLIGHT = { fill: '#3949ab', fillOp: 0.45, stroke: '#283593', strokeW: 3 };
const SELECT_ZOOM = 20;

// Same palette as the rest of the UMaps tooling renders buildings with.
const POLY_COLORS = {
  outline:  { fill: '#ffffff', fillOp: 1,    stroke: '#aaaaaa', strokeW: 0   },
  room:     { fill: '#FCF8EF', fillOp: 0.85, stroke: '#dbdbdb', strokeW: 1.5 },
  stair:    { fill: '#c4956a', fillOp: 0.9,  stroke: '#a0724a', strokeW: 1.5 },
  elevator: { fill: '#b0bec5', fillOp: 0.9,  stroke: '#78909c', strokeW: 1.5 },
  blocked:  { fill: '#000000', fillOp: 0.9,  stroke: '#000000', strokeW: 1.5 },
  // Tunnels read as building interior, not as a separate brown thing: they are
  // corridors like any other, and the route line is what says "you are
  // underground". Same values as `outline` above — kept written out rather than
  // aliased so the palette stays a plain lookup table.
  tunnel:   { fill: '#ffffff', fillOp: 1,    stroke: '#aaaaaa', strokeW: 0   },
  parking:  { fill: '#66bb6a', fillOp: 0.4,  stroke: '#2e7d32', strokeW: 1.5 },
  // Washrooms, tinted like stairs and elevators are. Not a polygon TYPE — the
  // structure files know nothing about washrooms — but a room the places data
  // says is one (see `bathroomRooms` in lib/places.js). Deliberately a muted
  // blue rather than the route's #1a73e8: the same blue as the drawn path, even
  // as a pale fill, reads as part of the route at a glance.
  bathroom: { fill: '#a8d8f0', fillOp: 0.9,  stroke: '#4a90b8', strokeW: 1.5 },
};
const polyOpts = type => POLY_COLORS[type] ?? POLY_COLORS.room;

// UM Blue (UMapsTools' UMAPS_BRANDING_GUIDE.md §2), for a footprint that is the
// only thing drawn of its building.
//
// White is the right colour for a footprint with rooms on it: the rooms do not
// tile the whole shape — corridors, stairwells and the gaps between wings are
// the footprint showing through — so its fill is the paper the floor plan is
// printed on, and paper is white. With nothing on it, though, that same fill is
// just a white blob among fifty others on satellite imagery, saying only "a
// building is here" in the flattest way available. Blue makes the campus read
// as ours at a glance, and makes the handover to white mean something: colour
// is the map at rest, white is the map showing you the inside of something.
//
// A BRIGHT tint of UM Blue rather than the brand value itself: at full strength
// over satellite imagery the footprints were nearly black, and everything drawn
// on top of them — the route line, the pins, the building names — had to fight
// them for contrast. Bright, the campus still reads as ours and the things the
// user actually asked to see sit clearly above it.
//
// And slightly transparent, which flat colour cannot do: the satellite roof
// showing faintly through is what keeps a footprint attached to the building
// under it. Solid, a shape that is a few metres off — and some are — reads as
// its own object sitting beside the roof rather than as our outline of it.
const FOOTPRINT_BLUE = '#57b4f5';
const FOOTPRINT_BLUE_OPACITY = 0.5;
// The outline the transparent fill needs. Below ~0.6 the fill stops holding an
// edge of its own against satellite imagery — two adjacent wings blur into one
// shape — and the stroke is what puts the boundary back. Dark blue rather than
// a darker tint of the fill: it is the same navy the bus pins are drawn in, so
// the things this app draws over campus read as one hand.
const FOOTPRINT_STROKE = '#0d3b66';

/**
 * The style for one drawn polygon. Washrooms win over the plain `room` fill;
 * a stair or elevator keeps its own colour even if a washroom shares its number,
 * because the shaft is the thing a router has to reason about.
 */
function polyStyleFor(poly, slug, bathroomRooms) {
  if (poly.type === 'room' && poly.roomNumber && bathroomRooms
      && bathroomRooms.has(`${slug}::${String(poly.roomNumber).toLowerCase()}`)) {
    return POLY_COLORS.bathroom;
  }
  return polyOpts(poly.type);
}

const CHEVRON_REPEAT_PX = 80;
// Each tick rewrites the `icons` array of every highlighted polyline, and in
// preview EVERY leg is highlighted — a cross-campus route is nine of them. Half
// the ticks with twice the step is the same 50 px/s of flow for half the work.
const CHEVRON_TICK_MS = 120;
const CHEVRON_STEP_PX = 6;
// Navigation opens as close as the app ever goes — the same level selecting a
// room uses. The first instruction is "walk out of this room and turn", which
// needs the doorway and the corridor outside it legible, not the block.
const START_ZOOM = SELECT_ZOOM;
const USER_LOCATE_ZOOM = 18; // fly-to level when the user's location first resolves
// Bounds for the places overview. Fitting the raw spread of pins pulls the map
// out past the whole city, and campus is about as wide as is ever useful — but
// the opposite happens too: a dozen bathrooms in one building span 60 m, and
// fitting *that* would zoom to street level, which is no overview at all. So
// the fit is clamped at both ends.
const PLACES_MIN_ZOOM = 16;
const PLACES_MAX_ZOOM = 18;
// The route overview gets the same treatment, and for the same reason: fitting
// a cross-campus walk exactly would pull back until the buildings it runs
// between are unreadable specks, which is the least useful moment to lose
// detail. Clamped, the camera mostly just MOVES to the route and only gives up
// a level or two of detail when the walk is genuinely long — the far end can
// sit off screen, because the step list and the GO flow lead there anyway.
const ROUTE_MIN_ZOOM = 16;
const ROUTE_MAX_ZOOM = 18;
// At or below this the map is an overview, so every study spot and library
// shows; above it they retire and the Nearby list takes over.
const OVERVIEW_PINS_MAX_ZOOM = 17;
// At or above this the map is at walking scale, where a turn arrow marks the
// spot it happens rather than piling onto its neighbours.
const STEP_ICONS_MIN_ZOOM = 18;
// Zoom changes smaller than this aren't worth animating (or doing at all).
const ZOOM_DEADBAND = 1;
// However far the camera has to travel, spend at most this many ticks on it.
const ZOOM_MAX_TICKS = 3;
// Beyond this the user is not on campus as far as the overview is concerned.
const USER_IN_FRAME_METERS = 2000;

const ROUTE_COLOR = '#1a73e8';  // normal walking path
const TUNNEL_COLOR = '#1a73e8'; // tunnels drawn same blue as the rest of the path

// Gold binoculars pin (with a "Look" tab) marking a route intersection panorama.
const BINOCULARS_SVG =
  `<svg xmlns="http://www.w3.org/2000/svg" width="52" height="48" viewBox="0 0 52 48">` +
  `<rect x="11" y="1.5" width="30" height="15" rx="7.5" fill="#5b3a00"/>` +
  `<text x="26" y="12.3" font-family="-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif" font-size="9.5" font-weight="700" fill="#fff" text-anchor="middle">Look</text>` +
  `<circle cx="26" cy="32" r="13.5" fill="#fcb40c" stroke="#5b3a00" stroke-width="2"/>` +
  `<g fill="#5b3a00"><rect x="16.5" y="29" width="7" height="11" rx="3"/><rect x="28.5" y="29" width="7" height="11" rx="3"/>` +
  `<rect x="22.5" y="31" width="7" height="3.5"/><rect x="17.5" y="25.5" width="5" height="5" rx="1.5"/><rect x="29.5" y="25.5" width="5" height="5" rx="1.5"/></g></svg>`;
function binocularsIcon() {
  return {
    url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(BINOCULARS_SVG),
    scaledSize: new window.google.maps.Size(52, 48),
    anchor: new window.google.maps.Point(26, 32),
  };
}

// Map pins for the "More Places" panel: a white teardrop carrying a glyph in
// the place's colour. Study spots and libraries use the reading figure (a head
// over an open book); bathrooms get a restroom glyph, since a person reading
// would say nothing useful about a washroom. Bathroom pins take Welcome Day's
// per-type colours so the two features agree on what a washroom looks like.
const PLACE_PIN_COLORS = {
  study: '#3949ab',
  library: '#6d4c41',
  // Per washroom type, matching Welcome Day and the panel's chips.
  'bathroom-mens': '#0060a8',
  'bathroom-womens': '#e5556a',
  'bathroom-universal': '#8e24aa',
  'amenity:microwave': '#2e7d32',
  // The bus pin is FILLED with this rather than outlined in it, so it is a
  // dark blue where the others are mid-tone: it carries a white glyph, and it
  // has to hold up against the bright footprints and the asphalt alike.
  busstop: '#0d3b66',
};

// Person reading an open book, traced from the UMaps study-spot icon. Drawn as
// strokes on a 512 grid so it scales down to pin size without filling in.
const READING_GLYPH =
  '<g fill="none" stroke="COLOR" stroke-width="34" stroke-linecap="round" stroke-linejoin="round">' +
  '<circle cx="256" cy="104" r="86"/>' +
  '<path d="M142 252c30-34 70-54 114-54s84 20 114 54"/>' +
  '<path d="M74 232 256 292 438 232"/>' +
  '<path d="M74 232v206l182 60 182-60V232"/>' +
  '<path d="M256 292v206"/>' +
  '<path d="M74 306H58a30 30 0 0 0 0 60h16"/>' +
  '<path d="M438 306h16a30 30 0 0 1 0 60h-16"/>' +
  '</g>';

// Washroom figures, one per type, matching PlaceIcons.jsx.
const FIGURE_M = '<circle cx="256" cy="83" r="45"/>' +
  '<path d="M256 145c-40 0-66 26-66 64v100h36V448h60V309h36V209c0-38-26-64-66-64z"/>';
const FIGURE_F = '<circle cx="256" cy="83" r="45"/>' +
  '<path d="M256 145c-38 0-62 21-73 58l-32 119h49l13-49v175h34v-111h18v111h34V273l13 49h49l-32-119c-11-37-35-58-73-58z"/>';
const BOTH_GLYPH =
  '<g transform="translate(-128 0) scale(0.86)">' + FIGURE_M + '</g>' +
  '<g transform="translate(164 0) scale(0.86)">' + FIGURE_F + '</g>';

// The same microwave PlaceIcons.jsx draws, on the 512 grid. Stroke 32 rather
// than the reading figure's 34: the keypad lines start closing up above that at
// the 21px the glyph is actually inset at.
const MICROWAVE_GLYPH =
  '<g fill="none" stroke="COLOR" stroke-width="32" stroke-linecap="round" stroke-linejoin="round">' +
  '<rect x="53" y="117" width="405" height="277" rx="32"/>' +
  '<rect x="102" y="171" width="209" height="171" rx="17"/>' +
  '<path d="M367 205h55M367 260h55"/>' +
  '</g>';

// The same bus PlaceIcons.jsx draws, on the 512 grid (×21.33). Head-on rather
// than side-on: a side view at pin size collapses into a rounded rectangle,
// while the split windscreen and the two wheels stay readable — checked by
// rendering it at 21px before it went in.
//
// STROKEW rather than the fixed 32 the other glyphs carry: the bus pin below is
// deliberately the smallest pin on the map, and at the 15px it insets this to,
// a 32 thins to under a pixel and the windscreen divider vanishes.
const BUS_GLYPH =
  '<g fill="none" stroke="COLOR" stroke-width="STROKEW" stroke-linecap="round" stroke-linejoin="round">' +
  '<rect x="98" y="75" width="316" height="277" rx="51"/>' +
  '<rect x="143" y="120" width="226" height="102" rx="19"/>' +
  '<path d="M256 120v102"/>' +
  '<path d="M147 277h47M318 277h47"/>' +
  '<circle cx="177" cy="403" r="32"/>' +
  '<circle cx="335" cy="403" r="32"/>' +
  '</g>';

// A bicycle, side on, in the same 512-unit space as the bus. Two wheels, the
// frame between them, and the saddle and bars carried well clear of the top
// tube on their posts — at 24px those two bars are what makes the drawing read
// as a bicycle rather than as a pair of circles, and anything closer to the
// frame merges into it at this stroke.
const BIKE_GLYPH =
  '<g fill="none" stroke="COLOR" stroke-width="STROKEW" stroke-linecap="round" stroke-linejoin="round">' +
  '<circle cx="130" cy="330" r="112"/>' +
  '<circle cx="382" cy="330" r="112"/>' +
  '<path d="M130 330h120"/>' +                       // chainstay
  '<path d="M250 330l-35-155M130 330l85-155"/>' +    // seat tube + seat stay
  '<path d="M215 175l130-10M250 330l95-165"/>' +     // top tube + down tube
  '<path d="M382 330l-37-165"/>' +                   // fork
  '<path d="M215 175l-13-75M162 92h86"/>' +          // seat post + saddle
  '<path d="M345 165l7-60M312 96h80"/>' +            // stem + handlebar
  '</g>';

const PIN_GLYPHS = {
  'bathroom-mens': `<g fill="COLOR">${FIGURE_M}</g>`,
  'bathroom-womens': `<g fill="COLOR">${FIGURE_F}</g>`,
  'bathroom-universal': `<g fill="COLOR">${BOTH_GLYPH}</g>`,
  'amenity:microwave': MICROWAVE_GLYPH,
  // No busstop entry: a stop is drawn by busPinIcon() below, which is a
  // different pin rather than this one with a different glyph in it.
};

// The system stack the rest of the map's drawn labels use.
const PIN_FONT = '-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif';

// Text inside an inline SVG is XML, and these labels carry data from an outside
// service. A bare `&` in a route name doesn't render as an ampersand — it makes
// the whole document unparseable and the pin disappears, which is a strange way
// to find out that a route was renamed.
const XML_ESC = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' };
const esc = (s) => String(s).replace(/[&<>"]/g, c => XML_ESC[c]);

/**
 * A bus stop's pin: small, narrow, and solid dark blue with a white bus.
 *
 * The inverse of every other pin here, on purpose. These are the only pins on
 * the map at rest — twenty-one of them, drawn before anybody has asked for
 * anything — so they have to sit *under* whatever the user does next rather
 * than compete with it, and a white teardrop is the loudest mark this app
 * makes. Solid and dark, a stop reads as a fixture of the map, the way a
 * printed timetable symbol does, and the moment a route or a Nearby pin appears
 * the white ones are unmistakably the answer to the question just asked.
 *
 * Narrow for a second reason: these are the only pins that show in numbers over
 * open ground, along a kerb, several to a block. A slim mark says "a stop is
 * here"; a fat one covers the corner it is trying to point at.
 *
 * The thin white edge is not a background — it is what keeps a dark pin legible
 * over the dark asphalt these all sit on.
 *
 * `lines` — `[{ route, when }]` from `pinDepartures()` — turns the pin into a
 * departure board: the next three buses, printed beside it. Close in, that is
 * the whole question ("do I run for this one, or is there another in four
 * minutes?") answered without a tap; pulled back it would be twenty-one boxes
 * of text over each other, so the host only supplies lines above a zoom
 * threshold (see `busPins` in PathfinderTab). The canvas grows to the RIGHT to
 * make room, exactly as the floor badge does, so the teardrop and its anchor do
 * not move when the times arrive, change, or go away.
 *
 * Three rows are taller than the teardrop, so the canvas also grows UPWARD by
 * whatever the box overhangs (`padTop`) and everything shifts down by the same
 * amount. Without it the top row is simply clipped off by the viewBox — and
 * growing downward instead would push the tip off the kerb it is pointing at.
 */
function busPinIcon(lines = null, scale = 1) {
  const fill = PLACE_PIN_COLORS.busstop;
  // A little heavier than the 32 that reads at 21px, because white on dark at
  // 15px loses the windscreen divider first — but only a little: rasterised at
  // the true 28px, anything past ~36 closes the wheels into solid dots and the
  // panes into blocks, and the bus becomes a smudge.
  const glyph = BUS_GLYPH.replace(/COLOR/g, '#fff').replace(/STROKEW/g, '34');
  const rows = (lines || []).slice(0, 3);
  // Widths from character counts rather than measured text: this builds a data
  // URL, so there is no DOM to measure in and no font loaded yet to measure
  // against. The two columns are sized off the widest row so the times line up
  // down the right edge — a column of ragged times reads as a list of numbers
  // rather than as "the next two".
  const wide = (list, per) => Math.max(...list.map(s => s.length), 0) * per;
  const routeW = wide(rows.map(r => r.route), 6.1);
  const whenW = wide(rows.map(r => r.when), 5.5);
  const boxW = rows.length ? Math.round(6 + routeW + 7 + whenW + 6) : 0;
  const boxH = rows.length ? 11 * rows.length + 6 : 0;
  const boxX = 26;                 // overlaps the teardrop's right edge, so the
  const boxY = 14 - boxH / 2;      // two read as one object rather than two
  const w = rows.length ? boxX + boxW + 1 : 28;
  // How far the box overhangs the top of the teardrop's own 37-unit canvas, at
  // three rows. Everything is drawn one group down by this much and the canvas
  // is that much taller, so the tip stays exactly where it was.
  const padTop = Math.max(0, 1 - boxY);
  const h = 37 + padTop;
  const label = rows.length
    ? `<rect x="${boxX}" y="${boxY}" width="${boxW}" height="${boxH}" rx="4" ` +
      `fill="${fill}" stroke="#fff" stroke-width="1.2"/>` +
      rows.map((r, i) => {
        const base = boxY + 3 + 11 * i + 8;
        return `<text x="${boxX + 6}" y="${base}" font-family="${PIN_FONT}" font-size="9.5" ` +
          `font-weight="700" fill="#fff">${esc(r.route)}</text>` +
          `<text x="${boxX + boxW - 6}" y="${base}" font-family="${PIN_FONT}" font-size="9.5" ` +
          `font-weight="600" fill="#bcd7ef" text-anchor="end">${esc(r.when)}</text>`;
      }).join('')
    : '';
  // Drawn once in natural units and then blown up by writing the scaled size
  // into the SVG's own width/height while the viewBox stays put. Scaling only
  // the marker's `scaledSize` would leave the browser rasterising a 28px image
  // and stretching it, which is exactly the soft, unreadable label this is
  // meant to fix; giving the SVG its real intrinsic size keeps the type crisp.
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${round1(w * scale)}" ` +
    `height="${round1(h * scale)}" viewBox="0 0 ${w} ${round1(h)}">` +
    `<g transform="translate(0 ${round1(padTop)})">` +
    label +
    `<path d="M14 35C14 35 25.5 18.4 25.5 13A11.5 11.5 0 1 0 2.5 13c0 5.4 11.5 22 11.5 22z" ` +
    `fill="${fill}" stroke="#fff" stroke-width="1.5"/>` +
    // 512 → 15px, centred on the head at (14,13).
    `<g transform="translate(6.5 5.5) scale(0.0293)">${glyph}</g>` +
    `</g></svg>`;
  return {
    url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
    scaledSize: new window.google.maps.Size(w * scale, h * scale),
    // The tip stays on the stop at every size — a pin that grows out of its own
    // point would slide off the kerb as you zoomed.
    anchor: new window.google.maps.Point(14 * scale, (35 + padTop) * scale),
  };
}

const round1 = (n) => Math.round(n * 10) / 10;

/**
 * Bike parking: a filled green disc with a white bicycle. A CIRCLE, not a pin.
 *
 * A teardrop says "the thing you asked for is under this point" — it is an
 * arrow, and every pin on this map is answering a question somebody just asked.
 * These five are not: like the bus stops they sit on the resting map before
 * anybody has said anything, and what they mark is not a point but a place you
 * ride up to and leave a bike. A disc states rather than points, which is also
 * why it can sit right on the rack's own position instead of hovering above it
 * with a tail — so the mark is where the thing is, and it never covers the path
 * that leads to it.
 *
 * Filled and dark for the same reason the bus pin is: drawn before anything is
 * asked for, it has to sit under whatever the user does next. The white ring is
 * not decoration — it is what keeps a dark disc legible over the dark paths and
 * grass these all sit on.
 */
function bikePinIcon() {
  const glyph = BIKE_GLYPH.replace(/COLOR/g, '#fff').replace(/STROKEW/g, '30');
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="34" height="34" viewBox="0 0 34 34">` +
    `<circle cx="17" cy="17" r="15" fill="${BIKE_COLOR}" stroke="#fff" stroke-width="2"/>` +
    // 512 → 23px, nudged up 1 because the glyph's own mass sits low (two wheels
    // against one thin saddle) and centring the box would look bottom-heavy.
    `<g transform="translate(5.5 4.6) scale(0.0449)">${glyph}</g>` +
    `</svg>`;
  return {
    url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
    scaledSize: new window.google.maps.Size(34, 34),
    // Centre, not a tip: a disc marks the spot it covers.
    anchor: new window.google.maps.Point(17, 17),
  };
}

// Map pins for the Nearby panel: a white teardrop carrying a glyph in the
// place's colour. Study spots and libraries use the reading figure (the same
// drawing the panel's rows use); washrooms use their type's figure and colour.
// `floorTag` (e.g. "1", "100") rides in a badge on the pin's shoulder. Which
// floor a washroom is on decides whether it is worth walking to at all, and the
// pin was the one place that never said. The canvas widens to the right to make
// room, so the teardrop and its anchor stay exactly where they were.
function placePinIcon(kind, floorTag = null) {
  // Every route to a bus-stop pin lands on the same drawing — the Nearby tab's
  // pins as much as the ones on the resting map — so a stop looks like one
  // thing wherever it turns up. (No floor badge either: a stop is outdoors.)
  if (kind === 'busstop') return busPinIcon();
  const fill = PLACE_PIN_COLORS[kind] || PLACE_PIN_COLORS.study;
  const glyph = (PIN_GLYPHS[kind] || READING_GLYPH).replace(/COLOR/g, fill);
  const label = floorTag == null || floorTag === '' ? null : `F${floorTag}`;
  const w = label ? 40 + Math.max(20, 9 + String(label).length * 6.5) : 40;
  const badge = label
    ? `<g><rect x="24" y="0" width="${w - 25}" height="15" rx="7.5" fill="${fill}" stroke="#fff" stroke-width="2"/>` +
      `<text x="${24 + (w - 25) / 2}" y="11.2" font-family="-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif" ` +
      `font-size="9.5" font-weight="700" fill="#fff" text-anchor="middle">${label}</text></g>`
    : '';
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="52" viewBox="0 0 ${w} 52">` +
    `<path d="M20 51C20 51 37 27 37 19A17 17 0 1 0 3 19c0 8 17 32 17 32z" ` +
    `fill="#fff" stroke="${fill}" stroke-width="2.5"/>` +
    // Glyph inset into the head of the teardrop: 512 → 21px, centred on (20,18).
    `<g transform="translate(9.5 7.5) scale(0.041)">${glyph}</g>` +
    badge +
    `</svg>`;
  return {
    url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
    scaledSize: new window.google.maps.Size(w, 52),
    anchor: new window.google.maps.Point(20, 51),
  };
}

// A direction step's glyph as a small map badge, so the arrow sits where the
// turn happens. The step already carries its own character (↰ ↱ ↑ 🪜 🛗 🚪 🏁
// 🚇 🚶), so this draws that rather than re-deriving a second icon vocabulary
// that could drift from the list's.
const STEP_ICON_BLUE = '#1a73e8';
// Selected inverts rather than merely recolouring: at map scale, among a line
// of identical white discs, a filled one is findable at a glance where a
// slightly different outline is not. It also grows, so the selection survives
// being read on a phone.
function stepIcon(glyph, selected = false) {
  const size = selected ? 40 : 30;
  const half = size / 2;
  const bg = selected ? STEP_ICON_BLUE : '#fff';
  const fg = selected ? '#fff' : STEP_ICON_BLUE;
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">` +
    (selected ? `<circle cx="${half}" cy="${half}" r="${half - 1}" fill="#fff" opacity="0.9"/>` : '') +
    `<circle cx="${half}" cy="${half}" r="${half - (selected ? 4 : 2.5)}" fill="${bg}" ` +
    `stroke="${selected ? '#fff' : STEP_ICON_BLUE}" stroke-width="2.5"/>` +
    `<text x="${half}" y="${half + (selected ? 7 : 5.5)}" ` +
    `font-family="-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif" ` +
    `font-size="${selected ? 19 : 15}" font-weight="800" fill="${fg}" text-anchor="middle">` +
    `${escapeXml(glyph)}</text></svg>`;
  return {
    url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
    scaledSize: new window.google.maps.Size(size, size),
    anchor: new window.google.maps.Point(half, half),
  };
}
function escapeXml(s) {
  return String(s ?? '').replace(/[&<>"']/g, c =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

// Split a leg into consecutive runs of same edge kind (tunnel vs. normal) so
// each run can be drawn in its own colour. leg.nodes[k]→[k+1] uses leg.edges[k].
function tunnelRuns(leg) {
  const nodes = leg.nodes || [];
  const edges = leg.edges || [];
  const L = nodes.length - 1;
  if (L < 1) return [];
  const runs = [];
  let start = 0;
  let cur = !!edges[0]?.tunnel;
  for (let s = 1; s < L; s++) {
    const tun = !!edges[s]?.tunnel;
    if (tun !== cur) {
      runs.push({ tunnel: cur, path: nodes.slice(start, s + 1) });
      start = s;
      cur = tun;
    }
  }
  runs.push({ tunnel: cur, path: nodes.slice(start) });
  return runs;
}

/** Default floor a building shows when the route doesn't dictate one. */
function defaultFloor(building) {
  return building.floors.find(f => f >= 1) ?? building.floors[0];
}


// Fly to a point AND land on it.
//
// `panTo` animates the centre, `setZoom` does not — it keeps whatever centre
// the map has at that instant. So panning and then animating the zoom cancels
// the pan mid-flight and leaves the map hanging wherever it got to, which reads
// as "it zoomed but didn't go there". Re-asserting the centre on every zoom
// step fixes it: the map converges on the target instead of racing it.
function flyTo(map, position, targetZoom, zoomAnimRef, { allowZoomOut = false } = {}) {
  if (zoomAnimRef.current) { clearInterval(zoomAnimRef.current); zoomAnimRef.current = null; }
  map.panTo(position);
  const current = map.getZoom() ?? 17;
  // Selecting something should never pull the camera back out; a fit-to-bounds
  // explicitly may.
  const target = allowZoomOut ? targetZoom : Math.max(current, targetZoom);
  const gap = target - current;
  // Moving beats zooming. A level or less is not worth an animation — the view
  // barely changes, but every step reloads tiles and redraws every overlay, so
  // small hops were paying the whole cost for nothing. Just glide there.
  if (Math.abs(gap) <= ZOOM_DEADBAND) { map.setCenter(position); return; }
  // Cap the animation at a few ticks however far it has to travel, taking
  // bigger bites instead of grinding through one level at a time.
  const ticks = Math.min(ZOOM_MAX_TICKS, Math.ceil(Math.abs(gap)));
  const per = gap / ticks;
  let i = 0;
  zoomAnimRef.current = setInterval(() => {
    i += 1;
    map.setZoom(i === ticks ? target : Math.round((current + per * i) * 10) / 10);
    map.setCenter(position);
    if (i >= ticks) { clearInterval(zoomAnimRef.current); zoomAnimRef.current = null; }
  }, 90);
}

// Pan to bounds center then animate zoom — gives a smooth "fly to" feel instead
// of the hard snap that fitBounds produces.
function smoothFitBounds(map, bounds, zoomAnimRef, padding = 80, minZoom = 0, maxZoom = 20) {
  const centre = bounds.getCenter();
  const div = map.getDiv();
  if (!div) { map.panTo(centre); return; }
  const ne = bounds.getNorthEast(), sw = bounds.getSouthWest();
  const dlat = Math.abs(ne.lat() - sw.lat());
  const dlng = Math.abs(ne.lng() - sw.lng());
  // A single point (or a degenerate box) has no extent to fit — just go there
  // at the closest allowed level rather than dividing by zero.
  if (dlat === 0 || dlng === 0) {
    flyTo(map, centre, maxZoom, zoomAnimRef, { allowZoomOut: true });
    return;
  }
  // World is 256px at zoom 0; each zoom level doubles pixel count.
  const availH = Math.max(1, div.offsetHeight - padding * 2);
  const availW = Math.max(1, div.offsetWidth  - padding * 2);
  const zLat = Math.log2(availH * 180 / (256 * dlat));
  const zLng = Math.log2(availW * 360 / (256 * dlng));
  const target = Math.max(minZoom, Math.min(Math.floor(Math.min(zLat, zLng)), maxZoom));
  // Through flyTo, NOT panTo + animateZoom: `setZoom` keeps whatever centre the
  // map has at that instant, so animating the zoom cancels the pan and strands
  // the camera partway — which is worse zooming out, and is why the places
  // overview landed off campus.
  flyTo(map, centre, target, zoomAnimRef, { allowZoomOut: true });
}

// SVG pill label used for the route start/end markers (replaces A/B circles).
// Long "Building Room#" labels are shortened from the building name so the room
// number always stays visible.
function roomLabelIcon(text, color) {
  const raw = String(text ?? '');
  const MAX = 26;
  let display = raw;
  if (raw.length > MAX) {
    const parts = raw.split(/\s+/);
    const tail = parts.length > 1 ? parts[parts.length - 1] : '';
    if (tail && /\d/.test(tail)) {                       // keep the trailing room number
      const head = raw.slice(0, raw.length - tail.length).trim();
      const keep = Math.max(3, MAX - tail.length - 2);
      display = (head.length > keep ? head.slice(0, keep) + '…' : head) + ' ' + tail;
    } else {
      display = raw.slice(0, MAX - 1) + '…';
    }
  }
  const esc = display.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  const w = Math.min(Math.max(64, display.length * 6.5 + 18), 200);
  const h = 26;
  const svg = [
    `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h + 7}">`,
    `<rect x="1" y="1" width="${w - 2}" height="${h - 2}" rx="6" fill="${color}"/>`,
    `<text x="${w / 2}" y="${h / 2 + 4}" font-family="-apple-system,BlinkMacSystemFont,sans-serif"`,
    ` font-size="10.5" font-weight="700" fill="#fff" text-anchor="middle">${esc}</text>`,
    `<polygon points="${w / 2 - 4},${h - 1} ${w / 2 + 4},${h - 1} ${w / 2},${h + 6}" fill="${color}"/>`,
    `</svg>`,
  ].join('');
  return {
    url: `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`,
    scaledSize: new window.google.maps.Size(w, h + 7),
    anchor: new window.google.maps.Point(w / 2, h + 7),
  };
}

// Pseudo-building slugs that don't have a human-readable campus address.
const PSEUDO_BUILDINGS = new Set(['outdoors', 'building_connections', 'tunnels']);

export function useRouteLayer({
  mapReady, mapInstanceRef, mapData, route, activeLeg, setActiveLeg, userPos,
  routeConfirmed,
  fromLabel, toLabel,    // short display strings for the start/end markers
  selectedRoom,          // getRoomLocation() result | null (destination/explored room)
  startRoom,             // same, for the room being walked FROM
  scanLoc,               // { nodeId, building, floor, position, label } | null — a scanned sign
  placeLoc,              // resolvePlace() result | null (study spot / library being previewed)
  placePins,             // [{ id, position, kind, title }] — the nearest handful, while zoomed in
  overviewPins,          // same shape — the whole selected kind, while zoomed out
  busPins,               // same shape — the campus bus stops, on the resting map
  bikePins,              // [{ id, position, name }] — bike racks, drawn as discs
  eventPins,             // [{ id, position, logo, title, when, tone }] — club events, this week

  parkingLots,           // parkingLots() from lib/parking.js — the priced areas + live status
  onPlacePin,            // (id) => select that place
  onBusPin,              // (id) => open that bus stop's sheet from the resting map
  onBikePin,             // (id) => open that bike rack's card
  onEventPin,            // (id) => open that event's sheet
  onLot,                 // (id) => open that parking lot's card; omitted leaves the lots inert
  fitPinsKey,            // bump to zoom out over the pins (see below)
  roomInfo,              // { title, subtitle } for the info card
  showRoomInfo,          // show the GO card (explore phase, before routing)
  onGoRoom, onCloseRoom, // info-card handlers
  onIntersection,        // (node, outgoingBearing) => void — tap an intersection with a panorama
  interiorsVersion = 0,  // bumped when a building's interior polygons arrive
  directions,            // buildDirections() output — drawn as turn icons up close
  activeStepId = null,   // the step highlighted in the list and on the map
  onStepIcon,            // (step) => void — tapped a turn icon on the map
}) {
  const polygonsRef = useRef(new Map()); // slug → Map<floor, google.maps.Polygon[]>
  const legLinesRef = useRef([]);
  const markersRef = useRef([]);
  const overlaysRef = useRef([]);
  const chevronTimerRef = useRef(null);
  const userMarkerRef = useRef(null);
  const highlightRef = useRef([]);       // selected-room highlight polygons
  const placeHighlightRef = useRef([]);  // previewed study spot / library
  const placePinsRef = useRef([]);       // pins for the whole nearby list
  const roomOverlayRef = useRef(null);   // the GO info card
  const zoomAnimRef = useRef(null);      // running zoom animation interval
  const didCenterUserRef = useRef(false);// one-time fly-to on first location fix
  const parkingLabelsRef = useRef([]);   // lot-name overlays on parking polygons

  // ── Building polygons ─────────────────────────────────────────────────────
  // Rebuilt when a building's interior arrives (interiorsVersion), not on every
  // render. This used to construct every polygon of every building up front —
  // 5,889 google.maps.Polygon objects, of which 146 were the outlines and the
  // rest the insides of buildings the user had shown no interest in. Now a
  // building contributes its footprint until something asks for more.
  // Only buildings whose polygons actually changed are rebuilt. A route can
  // pull in five interiors one after another, and rebuilding the whole campus
  // on each arrival — destroying and re-constructing every Polygon — made
  // previewing a cross-campus route stutter five times over. attachBuilding-
  // Interior swaps in a NEW polygonsByFloor Map, so identity is the signal.
  const builtFromRef = useRef(new Map()); // slug → the polygonsByFloor it was built from
  useEffect(() => {
    if (!mapReady || !mapData) return;
    const byBuilding = polygonsRef.current;
    const seen = new Set();
    for (const b of mapData.buildings) {
      seen.add(b.slug);
      if (builtFromRef.current.get(b.slug) === b.polygonsByFloor) continue; // unchanged
      // Replacing this building's set: drop the old objects first.
      const old = byBuilding.get(b.slug);
      if (old) for (const polys of old.values()) polys.forEach(p => p.setMap(null));
      const byFloor = new Map();
      for (const [floor, polys] of b.polygonsByFloor) {
        byFloor.set(floor, polys.map(p => {
          const st = polyStyleFor(p, b.slug, mapData.bathroomRooms);
          return new window.google.maps.Polygon({
          paths: p.ring,
          fillColor: st.fill,
          fillOpacity: st.fillOp,
          strokeColor: st.stroke,
          strokeWeight: st.strokeW,
          clickable: false,
          zIndex: p.type === 'outline' ? 10 : 20,
          map: null,
          // Read back by the floor effect to keep footprints always-on.
          pfaOutline: p.type === 'outline',
          });
        }));
      }
      byBuilding.set(b.slug, byFloor);
      builtFromRef.current.set(b.slug, b.polygonsByFloor);
    }
    // A whole new dataset: forget buildings that no longer exist.
    for (const slug of [...byBuilding.keys()]) {
      if (seen.has(slug)) continue;
      for (const polys of byBuilding.get(slug).values()) polys.forEach(p => p.setMap(null));
      byBuilding.delete(slug);
      builtFromRef.current.delete(slug);
    }
  }, [mapReady, mapData, interiorsVersion, mapInstanceRef]);

  // Teardown is its own effect so it runs on unmount only — the build effect
  // above must NOT drop everything each time an interior arrives.
  useEffect(() => () => {
    for (const byFloor of polygonsRef.current.values())
      for (const polys of byFloor.values()) polys.forEach(p => p.setMap(null));
    polygonsRef.current = new Map();
    builtFromRef.current = new Map();
  }, []);

  // ── Building name labels ──────────────────────────────────────────────────
  // Only for places the pathfinder actually knows — a name here is a promise
  // that you can route to it. White boxes that arrive as the map comes in (see
  // createBuildingLabelOverlay): pulled back, fifty of them tile over the
  // campus they are naming; in close, one box names one building.
  useEffect(() => {
    if (!mapReady || !mapData) return;
    const map = mapInstanceRef.current;
    const labels = [];

    const centroid = (rings) => {
      let lat = 0, lng = 0, n = 0;
      for (const ring of rings) for (const pt of ring) { lat += pt.lat; lng += pt.lng; n++; }
      return n ? { lat: lat / n, lng: lng / n } : null;
    };

    for (const b of mapData.buildings) {
      // Parking is named by its own labels below, which say what each lot costs
      // right now — a second plain name on the same shape would be the same
      // word twice, one of them less useful.
      if (b.slug === 'parking' || PSEUDO_BUILDINGS.has(b.slug)) continue;
      // Placed on the footprint, which every building has from the outlines
      // bundle whether or not its interior has been fetched.
      const at = centroid(b.outlines || []);
      if (at && b.name) labels.push(createBuildingLabelOverlay(map, at, b.name, { kind: 'building' }));
    }

    parkingLabelsRef.current = labels;
    return () => { labels.forEach(l => l.remove()); parkingLabelsRef.current = []; };
  }, [mapReady, mapData, mapInstanceRef]);

  // ── Parking lots ──────────────────────────────────────────────────────────
  //
  // The same lots /parking draws, coloured by who they are for and labelled
  // with what each one is doing at this hour — because on this campus that is
  // not a property of the lot, it is a property of the time: the permit lot you
  // cannot use at 2pm is open to anyone at 5.
  //
  // No filters, unlike that page. There the lots are the subject and controls
  // are worth it; here they are context around a walk, so the map just answers
  // the standing question and gets out of the way.
  //
  // A lot IS tappable, and opens its own card. The label answers "what is this
  // doing now"; the card answers the rest of it — the whole tariff, the hours
  // behind the badge, the time limit, and the HonkMobile zone you type into the
  // app at the machine, which is on no sign the map can show. The tap is taken
  // by the POLYGON: the label sits at the lot's centroid with `pointer-events:
  // none`, so a tap on the plate falls through to the shape under it and both
  // open the same card, without the label having to move to the one overlay
  // pane that receives DOM events (which is above the pins, and would put a
  // parking plate over every bus stop).
  //
  // A pin still wins the tap wherever the two overlap — markers sit above
  // polygons in the hit test — which was the original worry about making these
  // clickable, and is why it turns out to be safe. Clicks are switched off
  // entirely while a route is on screen: the lots stay as colour, but a tap on
  // the ground during navigation means "look at the map", not "tell me about
  // parking".
  //
  // Reconciled by id like the bus pins: the polygons never change, and the
  // badges change twice a day, so the minute-by-minute recompute upstream
  // almost always ends here as no DOM work at all.
  const lotPolysRef = useRef(new Map());   // id → Polygon
  const lotLabelsRef = useRef(new Map());  // id → overlay
  // Read inside the click handler so a new callback never rebuilds the shapes.
  const onLotRef = useRef(onLot);
  useEffect(() => { onLotRef.current = onLot; }, [onLot]);
  useEffect(() => {
    if (!mapReady) return undefined;
    const map = mapInstanceRef.current;
    const polys = lotPolysRef.current;
    const labels = lotLabelsRef.current;
    const wanted = new Map((parkingLots || []).map(l => [l.id, l]));

    for (const [id, p] of polys) if (!wanted.has(id)) { p.setMap(null); polys.delete(id); }
    for (const [id, l] of labels) if (!wanted.has(id)) { l.remove(); labels.delete(id); }

    for (const [id, lot] of wanted) {
      if (!polys.has(id)) {
        const poly = new window.google.maps.Polygon({
          paths: lot.ring, map,
          fillColor: lot.colors.fill, fillOpacity: 0.22,
          strokeColor: lot.colors.stroke, strokeOpacity: 0.8, strokeWeight: 1.5,
          // Under the buildings (10) — a lot is ground, and where the two
          // overlap the building is the thing you are looking for.
          clickable: true, zIndex: 5,
        });
        poly.addListener('click', () => onLotRef.current?.(id));
        polys.set(id, poly);
      }
      const label = labels.get(id);
      if (!label) {
        labels.set(id, createParkingLotOverlay(map, lot.position, {
          name: lot.name, short: lot.short, rate: lot.rate, rateShort: lot.rateShort,
          colors: lot.colors, status: lot.status,
        }));
      } else {
        label.setStatus(lot.status);
      }
    }
    return undefined;
  }, [mapReady, parkingLots, mapInstanceRef]);

  // Taps off while a route owns the screen — and off entirely for a host that
  // passed no handler, which is the old "nothing is clickable" behaviour.
  useEffect(() => {
    const on = !!onLot && !route;
    lotPolysRef.current.forEach(p => p.setOptions({ clickable: on }));
  }, [onLot, route, parkingLots, mapReady]);

  // Which form the lot labels are in, decided on the map's **idle** — the same
  // moment the bus stops' departure labels turn over (PathfinderTab's `pass`),
  // at the same DETAIL_ZOOM.
  //
  // The threshold was always shared; the moment was not. Reading the zoom
  // inside the overlay's own draw() flipped the lots part-way through a pinch,
  // while the bus labels waited for the gesture to finish — one map, one
  // threshold, two visibly different instants, which is indistinguishable from
  // two different thresholds. Deciding here, once, is also the cheaper of the
  // two: fifteen labels are told the answer instead of each asking the map for
  // it on every frame of every pan.
  useEffect(() => {
    if (!mapReady) return undefined;
    const map = mapInstanceRef.current;
    if (!map) return undefined;
    const gmaps = window.google.maps;
    const apply = () => {
      const z = map.getZoom();
      const full = z == null || z >= DETAIL_ZOOM;
      lotLabelsRef.current.forEach(l => l.setDetail?.(full));
    };
    apply();
    const listener = gmaps.event.addListener(map, 'idle', apply);
    return () => gmaps.event.removeListener(listener);
  }, [mapReady, parkingLots, mapInstanceRef]);

  // Unmount only — the reconcile above must not drop the lots each time a
  // badge is recomputed.
  useEffect(() => () => {
    for (const p of lotPolysRef.current.values()) p.setMap(null);
    for (const l of lotLabelsRef.current.values()) l.remove();
    lotPolysRef.current = new Map();
    lotLabelsRef.current = new Map();
  }, []);

  // A route owns the map it zooms out to. Both sets of names are there to say
  // what campus is when nothing else is going on; over a route they are ~60
  // words competing with the one line the user asked to see. The lot POLYGONS
  // stay — they are colour, not text, and they say which side of the road to
  // walk down.
  useEffect(() => {
    parkingLabelsRef.current.forEach(l => l.setHidden?.(!!route));
    lotLabelsRef.current.forEach(l => l.setHidden?.(!!route));
  }, [route, mapData, mapReady, parkingLots]);

  // ── Per-building displayed floor ──────────────────────────────────────────
  // A building is "in play" when the route runs through it, or the user has
  // selected a room or place inside it. Only those show their insides; every
  // other building shows its footprint alone. Previously each of the ~38
  // buildings had a whole floor of rooms on the map at once, all the time,
  // which is most of what made panning heavy.
  useEffect(() => {
    if (!mapReady || !mapData) return;
    const map = mapInstanceRef.current;
    // Every building the route passes through, at the floor the route uses
    // there — not just the leg being shown. A leg ends at each floor change, so
    // reading only the active one left the rest of the walk sitting over blank
    // footprints: preview a route from Helen Glass to Stanley Pauley and
    // everything past the first staircase had no inside at all.
    //
    // Earliest leg wins, so a building shows the floor you ENTER it on rather
    // than one you reach later — walking into EITC E3 on F1 and taking the
    // stairs to F3, it is F1 that you need to see. The active leg then
    // overrides, so once navigating, the building you are in follows you up.
    const routeFloors = new Map();
    if (route) {
      for (const leg of route.legs) {
        for (const [slug, floor] of legFloors(leg)) {
          if (!routeFloors.has(slug)) routeFloors.set(slug, floor);
        }
      }
      for (const [slug, floor] of legFloors(route.legs[activeLeg] ?? route.legs[0])) {
        routeFloors.set(slug, floor);
      }
    }
    for (const b of mapData.buildings) {
      // The lot layer above draws these from the same file, in the colour of
      // who each lot is for. Drawing both would stack two polygons on every lot
      // — the plain green one darkening a shape whose colour now means
      // something.
      if (b.slug === 'parking' && parkingLots?.length) {
        const byFloorP = polygonsRef.current.get(b.slug);
        if (byFloorP) for (const polys of byFloorP.values()) polys.forEach(p => p.setMap(null));
        continue;
      }
      let show = routeFloors.get(b.slug);
      if (show == null && selectedRoom && selectedRoom.building === b.slug) show = selectedRoom.floor;
      if (show == null && placeLoc && placeLoc.building === b.slug) show = placeLoc.floor;
      // A scanned sign, last: it says where somebody IS, and everything above it
      // says where they are going or looking, which is the better answer once
      // they have asked for one. Until then this is the only thing on screen
      // that knows which floor to draw.
      if (show == null && scanLoc && scanLoc.building === b.slug) show = scanLoc.floor;
      const inPlay = show != null;
      if (show == null) show = defaultFloor(b);
      const byFloor = polygonsRef.current.get(b.slug);
      if (!byFloor) continue;
      for (const [floor, polys] of byFloor) {
        // Still one floor at a time: most buildings carry an outline on EVERY
        // floor, so "always draw footprints" would stack ~4 copies each.
        if (floor !== show) { polys.forEach(p => p.setMap(null)); continue; }
        polys.forEach(p => {
          // The footprint is what makes campus legible before anyone has
          // searched for anything, so it draws regardless; the rooms inside
          // only once this building is part of what the user asked for.
          const isOutline = p.get('pfaOutline');
          const visible = isOutline || inPlay;
          // Blue while the footprint is on its own, white the moment rooms are
          // drawn over it — see FOOTPRINT_BLUE. Written only when it actually
          // changes: this effect runs on every route, leg and selection change,
          // and re-setting the same fill on ~150 polygons each time makes Maps
          // redraw the lot.
          if (isOutline && p.get('pfaBlue') !== !inPlay) {
            p.set('pfaBlue', !inPlay);
            p.setOptions({
              fillColor: inPlay ? POLY_COLORS.outline.fill : FOOTPRINT_BLUE,
              // Opaque again the moment rooms are drawn over it: the footprint
              // is then the paper the floor plan is printed on, and a plan you
              // can see a satellite roof through is a plan you cannot read.
              fillOpacity: inPlay ? POLY_COLORS.outline.fillOp : FOOTPRINT_BLUE_OPACITY,
              // The outline goes with the transparency — and goes away with it
              // too, since a plan's own room strokes draw the edges once the
              // building is opened.
              strokeColor: inPlay ? POLY_COLORS.outline.stroke : FOOTPRINT_STROKE,
              strokeOpacity: inPlay ? 1 : 0.9,
              strokeWeight: inPlay ? POLY_COLORS.outline.strokeW : 1.5,
            });
          }
          p.setMap(visible ? map : null);
        });
      }
    }
  }, [mapReady, mapData, interiorsVersion, route, activeLeg, selectedRoom, placeLoc, scanLoc, parkingLots, mapInstanceRef]);

  // ── Start-room highlight ──────────────────────────────────────────────────
  // Its own effect rather than folded into the destination's: that one also
  // flies the camera and puts up the GO card, neither of which belongs to the
  // room you are leaving.
  const startHighlightRef = useRef([]);
  useEffect(() => {
    if (!mapReady) return undefined;
    const map = mapInstanceRef.current;
    const clear = () => {
      startHighlightRef.current.forEach(p => p.setMap(null));
      startHighlightRef.current = [];
    };
    clear();
    if (!startRoom?.rings?.length) return clear;
    startHighlightRef.current = startRoom.rings.map(ring => new window.google.maps.Polygon({
      paths: ring, map,
      fillColor: START_HIGHLIGHT.fill, fillOpacity: START_HIGHLIGHT.fillOp,
      strokeColor: START_HIGHLIGHT.stroke, strokeWeight: START_HIGHLIGHT.strokeW,
      clickable: false, zIndex: 50,
    }));
    return clear;
  }, [mapReady, startRoom, mapInstanceRef]);

  // ── A scanned sign: mark the spot, and take the map there ─────────────────
  //
  // Two markers on one point, deliberately. The pill says WHAT this is in words
  // and floats clear of the map; the dot under it says WHICH POINT, to the
  // metre, which is the whole reason a sign beats geolocation indoors. The pill
  // alone (what the route's endpoints use) is anchored by its tail and reads as
  // "somewhere around here", and around here is a corridor junction where three
  // ways look alike.
  //
  // The camera move happens ONCE per sign, keyed on the node. Re-flying on every
  // render would fight the person trying to pan away from it, and a map that
  // snaps back is a map that feels broken.
  const scanMarkersRef = useRef([]);
  const flownScanRef = useRef('');
  useEffect(() => {
    if (!mapReady) return undefined;
    const map = mapInstanceRef.current;
    const clear = () => {
      scanMarkersRef.current.forEach(m => m.setMap(null));
      scanMarkersRef.current = [];
    };
    clear();
    // Once a route is drawn the green Start pill marks this very node, and two
    // labels stacked on one point is one label too many.
    if (!scanLoc || route) return clear;

    scanMarkersRef.current = [
      new window.google.maps.Marker({
        position: scanLoc.position, map, zIndex: 690,
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE, scale: 8,
          fillColor: SCAN_COLOR, fillOpacity: 1, strokeColor: '#fff', strokeWeight: 3,
        },
        title: scanLoc.label || 'You are here',
      }),
      new window.google.maps.Marker({
        position: scanLoc.position, map, zIndex: 691,
        icon: roomLabelIcon('You are here', SCAN_COLOR),
        title: scanLoc.label || 'You are here',
      }),
    ];

    if (flownScanRef.current !== scanLoc.nodeId) {
      flownScanRef.current = scanLoc.nodeId;
      // Room-level, the same zoom as picking a room: close enough that the
      // polygons around the dot are the corridor and the doors off it.
      flyTo(map, scanLoc.position, SELECT_ZOOM, zoomAnimRef);
    }
    return clear;
  }, [mapReady, scanLoc, route, mapInstanceRef]);

  // ── Selected-room highlight + fly-to + info card ──────────────────────────
  useEffect(() => {
    if (!mapReady) return;
    const map = mapInstanceRef.current;
    const clear = () => {
      highlightRef.current.forEach(p => p.setMap(null));
      highlightRef.current = [];
      roomOverlayRef.current?.remove();
      roomOverlayRef.current = null;
    };
    clear();
    if (!selectedRoom || !selectedRoom.position) return clear;

    if (selectedRoom.rings.length) {
      highlightRef.current = selectedRoom.rings.map(ring => new window.google.maps.Polygon({
        paths: ring, map,
        fillColor: HIGHLIGHT.fill, fillOpacity: HIGHLIGHT.fillOp,
        strokeColor: HIGHLIGHT.stroke, strokeWeight: HIGHLIGHT.strokeW,
        clickable: false, zIndex: 50,
      }));
    } else {
      highlightRef.current = [new window.google.maps.Marker({
        position: selectedRoom.position, map, zIndex: 50,
        icon: { path: window.google.maps.SymbolPath.CIRCLE, scale: 10, fillColor: HIGHLIGHT.fill, fillOpacity: 0.9, strokeColor: HIGHLIGHT.stroke, strokeWeight: 3 },
      })];
    }

    if (showRoomInfo) {
      // Same race as the place fly-to below — see flyTo().
      flyTo(map, selectedRoom.position, SELECT_ZOOM, zoomAnimRef);
      roomOverlayRef.current = createRoomInfoOverlay(map, selectedRoom.position, {
        title: roomInfo?.title || '',
        subtitle: roomInfo?.subtitle || '',
        roomType: roomInfo?.roomType || null,
        onGo: onGoRoom,
        onClose: onCloseRoom,
      });
    }
    return clear;
  }, [mapReady, selectedRoom, showRoomInfo, roomInfo, onGoRoom, onCloseRoom, mapInstanceRef]);

  // ── Study spot / library pins ─────────────────────────────────────────────
  // Shown while the list is open so the panel and the map agree on what's
  // around you. Tapping a pin is the same as tapping its row.
  useEffect(() => {
    if (!mapReady) return;
    const map = mapInstanceRef.current;
    const clear = () => {
      placePinsRef.current.forEach(m => m.setMap(null));
      placePinsRef.current = [];
    };
    clear();
    if (!placePins?.length) return clear;

    const gmaps = window.google.maps;
    const markers = placePins.map(pin => {
      const marker = new gmaps.Marker({
        position: pin.position, map: null, zIndex: 45,
        title: pin.title,
        icon: placePinIcon(pin.kind, pin.floorTag),
      });
      if (onPlacePin) marker.addListener('click', () => onPlacePin(pin.id));
      return marker;
    });
    placePinsRef.current = markers;
    // These are the nearest handful; the overview set above is the whole kind.
    // They hand over at the same threshold so the two are never both on the
    // map, which would double up every pin they have in common.
    const apply = () => {
      const z = map.getZoom();
      const show = z == null || z > OVERVIEW_PINS_MAX_ZOOM || !overviewPins?.length;
      markers.forEach(m => m.setMap(show ? map : null));
    };
    apply();
    const listener = gmaps.event.addListener(map, 'zoom_changed', apply);
    return () => { gmaps.event.removeListener(listener); clear(); };
  }, [mapReady, placePins, overviewPins, onPlacePin, mapInstanceRef]);

  // ── Bus stops on the resting map ──────────────────────────────────────────
  //
  // The one kind of place that is drawn before anybody opens a panel, because it
  // is the one whose answer changes by the minute and whose position a person
  // already half knows: "is there a stop by that corner, and have I missed the
  // bus" is asked standing still, not searched for. Twenty-one pins is a legible
  // number at campus scale — the reason the same treatment would be wrong for
  // 126 washrooms.
  //
  // Only while the map is at rest. The host takes them away the moment a route
  // or the Nearby panel owns the screen (see `busPins` in PathfinderTab), so
  // these never sit on top of a walk or double up with the Bus stops tab's own
  // pins.
  // Reconciled rather than rebuilt, unlike every other pin set here, because
  // this one changes CONTENT on a timer: the departure labels are re-derived
  // every half minute, and tearing down twenty-one markers to put twenty-one
  // near-identical ones back would blink the whole set each time. Markers are
  // kept by id and only the icon is rewritten, and only when the label it would
  // draw actually differs — "6m" for the next thirty seconds is the same pin.
  const busPinsRef = useRef(new Map()); // id → marker
  useEffect(() => {
    if (!mapReady) return undefined;
    const map = mapInstanceRef.current;
    const live = busPinsRef.current;
    const wanted = new Map((busPins || []).map(p => [p.id, p]));

    for (const [id, m] of live) {
      if (!wanted.has(id)) { m.setMap(null); live.delete(id); }
    }
    for (const [id, pin] of wanted) {
      let m = live.get(id);
      if (!m) {
        m = new window.google.maps.Marker({ position: pin.position, map, zIndex: 44 });
        if (onBusPin) m.addListener('click', () => onBusPin(id));
        live.set(id, m);
      }
      const rows = pin.lines || [];
      const scale = pin.scale || 1;
      const sig = rows.map(r => `${r.route} ${r.when}`).join(' · ');
      // The size is part of what is drawn, so it has to be part of the
      // comparison — otherwise crossing the zoom that enlarges these would
      // leave every unchanged label at the old size until its times ticked.
      if (m.get('pfaSig') !== `${scale}|${sig}`) {
        m.set('pfaSig', `${scale}|${sig}`);
        m.setIcon(busPinIcon(rows, scale));
        // The hover title carries the times too — the label is the answer for
        // somebody looking at the map, and this is the same answer for somebody
        // who cannot read 9.5px type on a phone at arm's length.
        m.setTitle(sig ? `${pin.title} — ${sig}` : pin.title);
      }
    }
    return undefined;
  }, [mapReady, busPins, onBusPin, mapInstanceRef]);

  // Teardown on unmount only: the reconcile above must not drop the set every
  // time a label ticks over.
  useEffect(() => () => {
    for (const m of busPinsRef.current.values()) m.setMap(null);
    busPinsRef.current = new Map();
  }, []);

  // ── Bike parking on the resting map ───────────────────────────────────────
  //
  // Drawn beside the bus stops and under the same rule: it is where you leave
  // the thing you arrived on, so it belongs to the resting map rather than to a
  // panel you have to know to open. Five discs cost the map nothing, and the
  // question they answer ("can I lock up near here?") is one you have while
  // already moving, which is exactly when nobody is typing a search.
  //
  // Rebuilt rather than reconciled — unlike the bus pins these never change
  // what they draw, so the set only ever changes when the host puts it away for
  // a route or the Nearby sheet, and rebuilding five markers then is free.
  //
  // zIndex 43: below the bus stops (44) and well below the Nearby pins (45), so
  // the resting furniture never covers an answer.
  const bikePinsRef = useRef([]);
  useEffect(() => {
    if (!mapReady) return undefined;
    const map = mapInstanceRef.current;
    const clear = () => {
      bikePinsRef.current.forEach(m => m.setMap(null));
      bikePinsRef.current = [];
    };
    clear();
    if (!bikePins?.length) return clear;
    const icon = bikePinIcon();     // identical for every one — built once
    bikePinsRef.current = bikePins.map(pin => {
      const m = new window.google.maps.Marker({
        position: pin.position, map, zIndex: 43, title: pin.title || pin.name, icon,
      });
      if (onBikePin) m.addListener('click', () => onBikePin(pin.id));
      return m;
    });
    return clear;
  }, [mapReady, bikePins, onBikePin, mapInstanceRef]);

  // ── Club events on the resting map ────────────────────────────────────────
  //
  // Drawn with the bus stops and the bike racks, and for a related reason: an
  // event has an expiry, and somebody crossing campus on the day it happens
  // should not have had to know to look for it. The host caps the set at the
  // coming week and takes it away for a route or a sheet — see `eventPins` in
  // PathfinderTab.
  //
  // These are DOM overlays rather than Markers because each one draws a club's
  // logo from a URL, which a Marker icon cannot do (see eventPinOverlay.js).
  // Rebuilt rather than reconciled: the set only changes when an event ends or
  // the host puts it away, which is minutes apart, and the label on one never
  // changes while it is on screen — a date does not tick.
  //
  // Above every other resting pin (the overlay's own CSS z-index), because it
  // is the only one that is about *now* and the only one somebody may not know
  // to look for.
  const eventPinsRef = useRef([]);
  useEffect(() => {
    if (!mapReady) return undefined;
    const map = mapInstanceRef.current;
    const clear = () => {
      eventPinsRef.current.forEach(p => p.remove());
      eventPinsRef.current = [];
    };
    clear();
    if (!eventPins?.length) return clear;
    eventPinsRef.current = eventPins.map(pin => createEventPinOverlay(map, pin.position, {
      logo: pin.logo, title: pin.title, when: pin.when, tone: pin.tone,
      onTap: () => onEventPin?.(pin.id),
    }));
    return clear;
  }, [mapReady, eventPins, onEventPin, mapInstanceRef]);

  // ── The whole selected kind, while zoomed out ─────────────────────────────
  // Pulled back you are asking "where can I sit?" and the answer is the shape
  // of the whole set, so these take over from the nearest-handful pins below
  // that zoom. The host decides whether there is a set at all — they only exist
  // while the Nearby panel is open on a study or library tab.
  const overviewPinsRef = useRef([]);
  useEffect(() => {
    if (!mapReady || !overviewPins?.length) return undefined;
    const map = mapInstanceRef.current;
    const gmaps = window.google.maps;
    const markers = overviewPins.map((pin) => {
      const m = new gmaps.Marker({
        position: pin.position, map: null, zIndex: 40,
        title: pin.title,
        icon: placePinIcon(pin.kind),
      });
      if (onPlacePin) m.addListener('click', () => onPlacePin(pin.id));
      return m;
    });
    overviewPinsRef.current = markers;
    const apply = () => {
      const z = map.getZoom();
      const show = z != null && z <= OVERVIEW_PINS_MAX_ZOOM;
      markers.forEach(m => m.setMap(show ? map : null));
    };
    apply();
    const listener = gmaps.event.addListener(map, 'zoom_changed', apply);
    return () => {
      gmaps.event.removeListener(listener);
      markers.forEach(m => m.setMap(null));
      overviewPinsRef.current = [];
    };
  }, [mapReady, overviewPins, onPlacePin, mapInstanceRef]);

  // ── Turn icons along the route, while zoomed in ───────────────────────────
  // The mirror of the overview pins: close in, each instruction gets its arrow
  // where it happens, so the step list and the map say the same thing without
  // the user having to hold the list in their head. Pulled back they would
  // collide into an unreadable pile, so they wait until the map is at
  // walking scale.
  const stepIconsRef = useRef([]);
  useEffect(() => {
    if (!mapReady || !directions?.length || !route) return undefined;
    const map = mapInstanceRef.current;
    const gmaps = window.google.maps;
    // Only the leg being drawn. A step's position says nothing about which
    // floor it is on, so every leg's arrows landed on the map at once and the
    // ones from other floors sat on top of the path you were actually walking —
    // a left turn from the third floor hovering over a second-floor corridor.
    const onPath = new Set((route.legs[activeLeg] ?? route.legs[0])?.nodes.map(n => n.id) || []);
    const shown = directions.filter(d => d.at && d.icon && d.nodeId && onPath.has(d.nodeId));
    const markers = shown.map((d) => {
      const m = new gmaps.Marker({
        position: d.at, map: null,
        // Above the route line and the binoculars pins, since it is the thing
        // being pointed at once selected.
        zIndex: d.id === activeStepId ? 640 : 615,
        title: d.text,
        icon: stepIcon(d.icon, d.id === activeStepId),
      });
      if (onStepIcon) m.addListener('click', () => onStepIcon(d));
      return m;
    });
    stepIconsRef.current = markers;
    const apply = () => {
      const z = map.getZoom();
      const show = z != null && z >= STEP_ICONS_MIN_ZOOM;
      markers.forEach(m => m.setMap(show ? map : null));
    };
    apply();
    const listener = gmaps.event.addListener(map, 'zoom_changed', apply);
    return () => {
      gmaps.event.removeListener(listener);
      markers.forEach(m => m.setMap(null));
      stepIconsRef.current = [];
    };
  }, [mapReady, directions, route, activeLeg, activeStepId, onStepIcon, mapInstanceRef]);

  // Zoom out to take in the pins — but only when the host asks (opening the
  // list, or coming back to it from a place), never on every pin change:
  // `placePins` is rebuilt on each keystroke of the filter, and refitting the
  // map under someone as they type would be miserable. Hence the nonce, and
  // hence placePins being deliberately absent from the deps.
  useEffect(() => {
    if (!mapReady || !fitPinsKey) return;
    const map = mapInstanceRef.current;
    const pins = placePinsRef.current;
    if (!map || !pins.length) return;
    const bounds = new window.google.maps.LatLngBounds();
    pins.forEach(m => bounds.extend(m.getPosition()));

    // Only frame the user along with the pins when they are actually near
    // them. A desktop browser geolocating by IP, or a bad first GPS fix, can
    // land kilometres away — and stretching the bounds to reach it centres the
    // overview halfway to nowhere, off campus entirely.
    if (userPos) {
      const centre = bounds.getCenter();
      const away = haversineMeters(userPos, { lat: centre.lat(), lng: centre.lng() });
      if (away <= USER_IN_FRAME_METERS) bounds.extend(userPos);
    }
    smoothFitBounds(map, bounds, zoomAnimRef, 90, PLACES_MIN_ZOOM, PLACES_MAX_ZOOM);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fitPinsKey, mapReady]);

  // ── Previewed place highlight + fly-to ────────────────────────────────────
  // Same treatment as a selected room but in the study-spot indigo, and with no
  // info card — the panel is already showing everything about it.
  useEffect(() => {
    if (!mapReady) return;
    const map = mapInstanceRef.current;
    const clear = () => {
      placeHighlightRef.current.forEach(p => p.setMap(null));
      placeHighlightRef.current = [];
    };
    clear();
    if (!placeLoc || !placeLoc.position) return clear;

    placeHighlightRef.current = placeLoc.rings.length
      ? placeLoc.rings.map(ring => new window.google.maps.Polygon({
          paths: ring, map,
          fillColor: PLACE_HIGHLIGHT.fill, fillOpacity: PLACE_HIGHLIGHT.fillOp,
          strokeColor: PLACE_HIGHLIGHT.stroke, strokeWeight: PLACE_HIGHLIGHT.strokeW,
          clickable: false, zIndex: 55,
        }))
      : [new window.google.maps.Marker({
          position: placeLoc.position, map, zIndex: 55,
          icon: { path: window.google.maps.SymbolPath.CIRCLE, scale: 10,
                  fillColor: PLACE_HIGHLIGHT.fill, fillOpacity: 0.9,
                  strokeColor: PLACE_HIGHLIGHT.stroke, strokeWeight: 3 },
        })];

    flyTo(map, placeLoc.position, SELECT_ZOOM, zoomAnimRef);
    return clear;
  }, [mapReady, placeLoc, mapInstanceRef]);

  // ── Route polylines + chevrons + floor-switch overlays ───────────────────
  useEffect(() => {
    if (!mapReady) return;
    const map = mapInstanceRef.current;
    const cleanup = () => {
      legLinesRef.current.forEach(pl => pl.setMap(null));
      legLinesRef.current = [];
      markersRef.current.forEach(m => m.setMap(null));
      markersRef.current = [];
      overlaysRef.current.forEach(o => o.remove());
      overlaysRef.current = [];
      if (chevronTimerRef.current) { clearInterval(chevronTimerRef.current); chevronTimerRef.current = null; }
    };
    cleanup();
    if (!route) return cleanup;

    const chevron = {
      path: window.google.maps.SymbolPath.FORWARD_OPEN_ARROW,
      scale: 2.4, strokeColor: '#ffffff', strokeWeight: 2.5, strokeOpacity: 0.95,
    };

    const activePolys = []; // highlighted polylines that carry the flowing chevrons
    route.legs.forEach((leg, i) => {
      // Preview (Stage 4): the WHOLE route is highlighted + animated.
      // Navigation (Stage 5): only the active leg is emphasized, others dimmed.
      const active = routeConfirmed ? (i === activeLeg) : true;
      // Each leg is split into tunnel / non-tunnel runs so tunnels draw golden.
      tunnelRuns(leg).forEach(run => {
        const pl = new window.google.maps.Polyline({
          path: run.path.map(n => ({ lat: n.lat, lng: n.lng })),
          strokeColor: run.tunnel ? TUNNEL_COLOR : ROUTE_COLOR,
          strokeOpacity: active ? 0.95 : 0.25,
          strokeWeight: active ? 6 : 3,
          zIndex: active ? 500 : 400,
          clickable: false,
          icons: active ? [{ icon: chevron, offset: '0px', repeat: `${CHEVRON_REPEAT_PX}px` }] : undefined,
          map,
        });
        legLinesRef.current.push(pl);
        if (active) activePolys.push(pl);
      });
    });
    if (activePolys.length) {
      let off = 0;
      chevronTimerRef.current = setInterval(() => {
        off = (off + CHEVRON_STEP_PX) % CHEVRON_REPEAT_PX;
        activePolys.forEach(pl => {
          const icons = pl.get('icons');
          if (icons && icons[0]) { icons[0].offset = `${off}px`; pl.set('icons', icons); }
        });
      }, CHEVRON_TICK_MS);
    }

    // Start / end markers — pill labels with the room name instead of A/B circles
    const endpointDefs = [
      { node: route.nodes[0], text: fromLabel || 'Start', color: '#34a853' },
      { node: route.nodes[route.nodes.length - 1], text: toLabel || 'End', color: '#e53935' },
    ];
    endpointDefs.forEach(({ node, text, color }) => {
      markersRef.current.push(new window.google.maps.Marker({
        position: { lat: node.lat, lng: node.lng }, map, zIndex: 600,
        icon: roomLabelIcon(text, color),
        title: text,
      }));
    });

    // The per-floor detail — enter/exit chips and the tap-to-switch-floors
    // popups — only appears in Navigation (Stage 5). During Route preview
    // (Stage 4) we keep the map clean: just the whole highlighted, animated
    // route with its start/end markers.
    if (routeConfirmed) {
      // Building enter/exit overlays: only at an *actual exit node* (type
      // 'exit') where the route crosses between a real building and the
      // outdoors. Tunnels aren't "outside" and aren't reached through exit
      // doors, so they never trigger an Enter/Exit chip.
      const isOutside = n => n.building !== 'tunnels' && (n.floor === 0 || PSEUDO_BUILDINGS.has(n.building));
      const legNodes = route.legs[activeLeg]?.nodes ?? [];
      for (let i = 0; i < legNodes.length - 1; i++) {
        const cur = legNodes[i];
        const nxt = legNodes[i + 1];
        const curOut = isOutside(cur);
        const nxtOut = isOutside(nxt);
        if (!curOut && nxtOut && cur.type === 'exit') {
          // Leaving a real building through its door — Exit chip at the door (cur)
          const name = !PSEUDO_BUILDINGS.has(cur.building)
            ? mapData?.buildings.find(b => b.slug === cur.building)?.name
            : null;
          if (name) {
            overlaysRef.current.push(
              createBuildingTransitionOverlay(map, { lat: cur.lat, lng: cur.lng }, { kind: 'exit', label: name })
            );
          }
        } else if (curOut && !nxtOut && nxt.type === 'exit') {
          // Entering a real building through its door — Enter chip at the door (nxt)
          const name = !PSEUDO_BUILDINGS.has(nxt.building)
            ? mapData?.buildings.find(b => b.slug === nxt.building)?.name
            : null;
          if (name) {
            overlaysRef.current.push(
              createBuildingTransitionOverlay(map, { lat: nxt.lat, lng: nxt.lng }, { kind: 'enter', label: name })
            );
          }
        }
      }

      const fwd = route.transitions[activeLeg];
      if (fwd) {
        overlaysRef.current.push(createFloorSwitchOverlay(map, fwd.fromNode, {
          label: `F${fwd.fromFloor} → F${fwd.toFloor}`,
          sub: 'tap to switch floors',
          icon: fwd.kind === 'elevator' ? '🛗' : '🪜',
          onTap: () => setActiveLeg(activeLeg + 1), // switch floors in place — don't move the map
        }));
      }
      const back = activeLeg > 0 ? route.transitions[activeLeg - 1] : null;
      if (back) {
        overlaysRef.current.push(createFloorSwitchOverlay(map, back.toNode, {
          label: `F${back.toFloor} → F${back.fromFloor}`,
          sub: 'tap to go back',
          icon: back.kind === 'elevator' ? '🛗' : '🪜',
          back: true,
          onTap: () => setActiveLeg(activeLeg - 1), // switch floors in place — don't move the map
        }));
      }

    }

    // Panorama pins on the active leg — shown whenever the route is on screen
    // (preview OR navigation), so a user can peek at a turn before committing.
    // The outgoing bearing is the direction the route leaves this node; the
    // viewer maps it against the photo's aligned heading.
    //
    // Any node carrying a photo gets a pin, not just nodes typed 'intersection'.
    // The type records whether the surveyor marked it a decision point, which
    // says nothing about whether the photo is useful to look at — gating on it
    // silently stranded a fifth of the panoramas with no way to open them.
    if (onIntersection) {
      const legNodes = route.legs[activeLeg]?.nodes ?? [];
      for (let i = 0; i < legNodes.length; i++) {
        const n = legNodes[i];
        if (!n.panorama?.image) continue;
        const nxt = legNodes[i + 1];
        const outBearing = nxt ? bearingDeg(n, nxt) : 0;
        const m = new window.google.maps.Marker({
          position: { lat: n.lat, lng: n.lng }, map, zIndex: 620,
          icon: binocularsIcon(),
          title: 'Intersection — tap to look around and see which way to go',
        });
        m.addListener('click', () => onIntersection(n, outBearing, nxt));
        markersRef.current.push(m);
      }
    }
    return cleanup;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapReady, mapData, route, activeLeg, routeConfirmed, fromLabel, toLabel, setActiveLeg, mapInstanceRef, onIntersection]);

  // ── Camera: fit route overview or zoom to start depending on confirmed state
  const navCamRef = useRef({ route: null, leg: 0, confirmed: false });
  useEffect(() => {
    if (!mapReady || !route) { navCamRef.current = { route: null, leg: 0, confirmed: false }; return; }
    const map = mapInstanceRef.current;
    const prev = navCamRef.current;
    const isNewRoute = prev.route !== route;
    navCamRef.current = { route, leg: isNewRoute ? 0 : activeLeg, confirmed: routeConfirmed };

    if (isNewRoute) {
      if (routeConfirmed) {
        // Rare: new route while already navigating (toggle changed mid-nav)
        const start = route.nodes[0];
        flyTo(map, { lat: start.lat, lng: start.lng }, START_ZOOM, zoomAnimRef, { allowZoomOut: true });
      } else {
        // Route just calculated: zoom out to show the full path
        const bounds = new window.google.maps.LatLngBounds();
        route.nodes.forEach(n => bounds.extend({ lat: n.lat, lng: n.lng }));
        smoothFitBounds(map, bounds, zoomAnimRef, 80, ROUTE_MIN_ZOOM, ROUTE_MAX_ZOOM);
      }
      return;
    }

    // Same route — check what changed
    if (prev.confirmed !== routeConfirmed) {
      if (routeConfirmed) {
        // User tapped GO — zoom in to the start
        const start = route.nodes[0];
        flyTo(map, { lat: start.lat, lng: start.lng }, START_ZOOM, zoomAnimRef, { allowZoomOut: true });
      } else {
        // User went back to overview — fit full route again
        const bounds = new window.google.maps.LatLngBounds();
        route.nodes.forEach(n => bounds.extend({ lat: n.lat, lng: n.lng }));
        smoothFitBounds(map, bounds, zoomAnimRef, 80, ROUTE_MIN_ZOOM, ROUTE_MAX_ZOOM);
      }
      return;
    }

    // Note: switching legs (tapping "switch floors") intentionally does NOT move
    // the map — the user stays oriented at their current view.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapReady, route, activeLeg, routeConfirmed, mapInstanceRef]);

  // ── User location dot ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!mapReady) return;
    const map = mapInstanceRef.current;
    if (!userPos) {
      userMarkerRef.current?.setMap(null);
      userMarkerRef.current = null;
      return;
    }
    if (!userMarkerRef.current) {
      userMarkerRef.current = new window.google.maps.Marker({
        map, zIndex: 700,
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE, scale: 8,
          fillColor: '#4285f4', fillOpacity: 1, strokeColor: '#fff', strokeWeight: 3,
        },
        title: 'Your location',
      });
    }
    userMarkerRef.current.setPosition(userPos);
    return () => {};
  }, [mapReady, userPos, mapInstanceRef]);

  // ── First location fix → fly to the user so they see where they are ─────────
  // Happens once. Skipped if a route is already framing the map (e.g. a shared
  // link opened straight into a route) so we don't yank the view away from it.
  useEffect(() => {
    if (!mapReady || !userPos || didCenterUserRef.current) return;
    didCenterUserRef.current = true;
    if (route) return;
    const map = mapInstanceRef.current;
    flyTo(map, userPos, USER_LOCATE_ZOOM, zoomAnimRef, { allowZoomOut: true });
  }, [mapReady, userPos, route, mapInstanceRef]);
}
